/**
 * Sales Service
 * Handles sales/promotions related operations
 */
import { getFirebaseDb } from '../firebase/firebase.js'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import logger from './logger.js'

const log = logger.createScoped('SalesService')

/**
 * Get all assortments as a lightweight index (for merakez selector)
 * Only returns id and name, stripping heavy items arrays
 * @returns {Promise<Array<{id: string, name: string|null}>>}
 */
export async function getAssortmentsIndex() {
  log.info('Fetching assortments index')

  try {
    const db = getFirebaseDb()
    const assortmentsRef = collection(db, 'Assortments')
    const snapshot = await getDocs(assortmentsRef)

    const assortments = snapshot.docs.map((doc) => {
      const data = doc.data()
      // Try to find a display name from common field names
      const name = data.custdes || data.name || data.representiveName || null
      return {
        id: doc.id,
        name,
      }
    })

    log.info('Assortments index loaded', { count: assortments.length })
    return assortments
  } catch (error) {
    log.error('Failed to fetch assortments index', error)
    throw error
  }
}

/**
 * Get all sales/promotions for a specific merakez
 * @param {string} merakez - The merakez ID to filter by
 * @returns {Promise<Array>}
 */
export async function getSalesByMerakez(merakez) {
  if (!merakez) {
    return []
  }

  log.info('Fetching sales for merakez', { merakez })

  try {
    const db = getFirebaseDb()
    const salesRef = collection(db, 'Sales')

    // Query sales by merakez, ordered by start date descending
    const q = query(salesRef, where('merakez', '==', merakez), orderBy('date_start_date', 'desc'))

    const snapshot = await getDocs(q)

    const sales = snapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        uid: data.uid || doc.id,
        merakez: data.merakez,
        active: data.active ?? false,
        approved: data.approved ?? false,
        first_order: data.first_order ?? false,
        weeklyBenefit: data.weeklyBenefit ?? false,
        label: data.label || '',
        description: data.description || '',
        saleType: data.saleType || '',
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        date_start_date: data.date_start_date || null,
        date_end_date: data.date_end_date || null,
        image: data.image || '',
        items: data.items || [],
        makats_arr: data.makats_arr || [],
        itemsCount: data.items?.length || data.makats_arr?.length || 0,
      }
    })

    log.info('Sales loaded for merakez', { merakez, count: sales.length })
    return sales
  } catch (error) {
    // If the query fails due to missing index, try without ordering
    if (error.code === 'failed-precondition') {
      return getSalesByMerakezUnordered(merakez)
    }
    log.error('Failed to fetch sales', error)
    throw error
  }
}

/**
 * Fallback: Get sales without ordering (if index is missing)
 * @param {string} merakez
 * @returns {Promise<Array>}
 */
async function getSalesByMerakezUnordered(merakez) {
  const db = getFirebaseDb()
  const salesRef = collection(db, 'Sales')

  const q = query(salesRef, where('merakez', '==', merakez))

  const snapshot = await getDocs(q)

  const sales = snapshot.docs.map((doc) => {
    const data = doc.data()
    return {
      id: doc.id,
      uid: data.uid || doc.id,
      merakez: data.merakez,
      active: data.active ?? false,
      approved: data.approved ?? false,
      first_order: data.first_order ?? false,
      weeklyBenefit: data.weeklyBenefit ?? false,
      label: data.label || '',
      description: data.description || '',
      saleType: data.saleType || '',
      start_date: data.start_date || '',
      end_date: data.end_date || '',
      date_start_date: data.date_start_date || null,
      date_end_date: data.date_end_date || null,
      image: data.image || '',
      items: data.items || [],
      makats_arr: data.makats_arr || [],
      itemsCount: data.items?.length || data.makats_arr?.length || 0,
    }
  })

  // Sort client-side by date_start_date or start_date descending
  sales.sort((a, b) => {
    const dateA = a.date_start_date?.toMillis?.() || new Date(a.start_date || 0).getTime()
    const dateB = b.date_start_date?.toMillis?.() || new Date(b.start_date || 0).getTime()
    return dateB - dateA
  })

  return sales
}

// ============================================
// Get Single Sale (for editing)
// ============================================

/**
 * Get a single sale by ID with all fields
 * @param {string} saleId
 * @returns {Promise<Object|null>}
 */
export async function getSaleById(saleId) {
  if (!saleId) return null

  log.info('Fetching sale by ID', { saleId })

  try {
    const db = getFirebaseDb()
    const saleRef = doc(db, 'Sales', saleId)
    const snapshot = await getDoc(saleRef)

    if (!snapshot.exists()) {
      log.warn('Sale not found', { saleId })
      return null
    }

    const data = snapshot.data()
    return {
      id: snapshot.id,
      ...data,
    }
  } catch (error) {
    log.error('Failed to fetch sale', error)
    throw error
  }
}

// ============================================
// Create Sale
// ============================================

/**
 * Create a new sale/promotion
 * @param {string} merakez - The merakez ID
 * @param {Object} payload - Sale data
 * @returns {Promise<string>} - The new document ID
 */
export async function createSale(merakez, payload) {
  if (!merakez) {
    throw new Error('Merakez is required')
  }

  log.info('Creating new sale', { merakez })

  try {
    const db = getFirebaseDb()
    const salesRef = collection(db, 'Sales')

    // Prepare the document data
    const saleData = {
      ...payload,
      merakez,
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    }

    // Ensure dates are properly formatted
    if (payload.start_date) {
      saleData.date_start_date = stringDateToTimestamp(payload.start_date)
    }
    if (payload.end_date) {
      saleData.date_end_date = stringDateToTimestamp(payload.end_date)
    }

    // Ensure makats_arr is in sync with items
    if (payload.items && Array.isArray(payload.items)) {
      saleData.makats_arr = payload.items.map((item) => item.makat)
    }

    const docRef = await addDoc(salesRef, saleData)

    // Update the document to set uid = doc.id
    await updateDoc(docRef, { uid: docRef.id })

    log.info('Sale created', { saleId: docRef.id })
    return docRef.id
  } catch (error) {
    log.error('Failed to create sale', error)
    throw error
  }
}

// ============================================
// Update Sale
// ============================================

/**
 * Update an existing sale/promotion
 * @param {string} saleId - The document ID
 * @param {Object} payload - Fields to update
 * @returns {Promise<void>}
 */
export async function updateSale(saleId, payload) {
  if (!saleId) {
    throw new Error('Sale ID is required')
  }

  log.info('Updating sale', { saleId })

  try {
    const db = getFirebaseDb()
    const saleRef = doc(db, 'Sales', saleId)

    // Prepare update data
    const updateData = {
      ...payload,
      updated_at: serverTimestamp(),
    }

    // Ensure dates are properly formatted
    if (payload.start_date !== undefined) {
      updateData.date_start_date = payload.start_date
        ? stringDateToTimestamp(payload.start_date)
        : null
    }
    if (payload.end_date !== undefined) {
      updateData.date_end_date = payload.end_date ? stringDateToTimestamp(payload.end_date) : null
    }

    // Ensure makats_arr is in sync with items
    if (payload.items && Array.isArray(payload.items)) {
      updateData.makats_arr = payload.items.map((item) => item.makat)
    }

    await updateDoc(saleRef, updateData)

    log.info('Sale updated', { saleId })
  } catch (error) {
    log.error('Failed to update sale', error)
    throw error
  }
}

// ============================================
// Delete Sale
// ============================================

/**
 * Delete a sale/promotion (hard delete)
 * @param {string} saleId - The document ID
 * @returns {Promise<void>}
 */
export async function deleteSale(saleId) {
  if (!saleId) {
    throw new Error('Sale ID is required')
  }

  log.info('Deleting sale', { saleId })

  try {
    const db = getFirebaseDb()
    const saleRef = doc(db, 'Sales', saleId)

    await deleteDoc(saleRef)

    log.info('Sale deleted', { saleId })
  } catch (error) {
    log.error('Failed to delete sale', error)
    throw error
  }
}

// ============================================
// Duplicate Sale
// ============================================

/**
 * Duplicate a sale/promotion to a different merakez
 * Copies all fields except id, uid, and timestamps
 * @param {string} saleId - The source sale document ID
 * @param {string} targetMerakez - The target merakez ID
 * @returns {Promise<string>} - The new document ID
 */
export async function duplicateSale(saleId, targetMerakez) {
  if (!saleId) throw new Error('Sale ID is required')
  if (!targetMerakez) throw new Error('Target merakez is required')

  log.info('Duplicating sale', { saleId, targetMerakez })

  const original = await getSaleById(saleId)
  if (!original) throw new Error('Sale not found')

  // Strip system fields - createSale will set new ones
  const { id: _id, uid: _uid, created_at: _ca, updated_at: _ua, merakez: _m, ...payload } = original

  const newId = await createSale(targetMerakez, payload)
  log.info('Sale duplicated', { originalId: saleId, newId, targetMerakez })
  return newId
}

// ============================================
// Helpers
// ============================================

/**
 * Convert a date string (YYYY-MM-DD) to Firestore Timestamp
 * Uses local timezone at 00:00
 * @param {string} dateStr
 * @returns {Timestamp|null}
 */
function stringDateToTimestamp(dateStr) {
  if (!dateStr) return null

  try {
    // Parse as local date at midnight
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day, 0, 0, 0, 0)
    return Timestamp.fromDate(date)
  } catch {
    return null
  }
}

/**
 * Convert Firestore Timestamp to date string (YYYY-MM-DD)
 * @param {Timestamp|null} timestamp
 * @returns {string}
 */
export function timestampToStringDate(timestamp) {
  if (!timestamp) return ''

  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  } catch {
    return ''
  }
}
